'use client';

/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';

import {
  Button,
  Container,
  FlexItem,
  FlexLayout,
  Form,
  Icon,
  IconContent,
  Link,
  Logo,
  overlay,
  SocialMediaGroup,
  TextField,
  Typography,
  useBreakPoints,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useTranslations } from 'next-intl';
import { IoIosSend } from 'react-icons/io';

import { ANALYTICS_EVENTS, trackEvent } from '@/analytics';
import { FaIcons } from '@/constants/faIcons';
import { SvgIcons } from '@/constants/iconPath';
import {
  handleInstagramClick,
  handleLinkedInClick,
  handleTwitterClick,
  handleYouTubeClick,
} from '@/constants/socialMediaClick';
import { useThemeMode } from '@/context';
import { HttpError } from '@/services/http/client';
import { subscribeToNewsletter } from '@/services/newsletter';
import { getResponseMessageKey } from '@/utils';
import { getScopedMessageKey } from '@/utils/getScopedMessageKey';

import type { NewsletterFormValues } from './NewsletterForm.types';
import { useStyles } from './Footer.styles';

const FOOTER_RESTORE_KEY = 'wci-refresh-at-footer';

const Footer = () => {
  const { isDark } = useThemeMode();
  const styles = useStyles(isDark);
  const t = useTranslations('Footer');
  const tAppMessages = useTranslations('APP_MESSAGES');
  const { isTablet, isDesktop } = useBreakPoints();

  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Remember only whether the user is currently inside the footer.
   * Other sections continue to use the browser's normal scroll restoration.
   */

  useEffect(() => {
    const footer = document.getElementById('site-footer');

    if (!footer) return;

    const updateFooterVisibility = () => {
      const rect = footer.getBoundingClientRect();

      const isFooterVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (isFooterVisible) {
        sessionStorage.setItem(FOOTER_RESTORE_KEY, window.location.pathname);

        return;
      }

      sessionStorage.removeItem(FOOTER_RESTORE_KEY);
    };

    updateFooterVisibility();

    window.addEventListener('scroll', updateFooterVisibility, {
      passive: true,
    });

    window.addEventListener('resize', updateFooterVisibility);

    return () => {
      window.removeEventListener('scroll', updateFooterVisibility);

      window.removeEventListener('resize', updateFooterVisibility);
    };
  }, []);

  const handleNewsletterSubscribeClick = () => {
    const eventData = {
      page: window.location.pathname,
      location: 'footer',
    };

    trackEvent(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_CLICKED, eventData);
  };

  const handleNewsletterSubmit = async (
    event: React.SyntheticEvent,
    formData: NewsletterFormValues,
    resetForm: () => void,
  ) => {
    event.preventDefault();

    if (isSubmitting) return;

    const attemptedEventData = {
      page: window.location.pathname,
      location: 'footer',
    };

    trackEvent(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_ATTEMPTED, attemptedEventData);

    setIsSubmitting(true);

    try {
      const response = await subscribeToNewsletter(formData);

      const subscribedEventData = {
        page: window.location.pathname,
        location: 'footer',
        httpStatus: 200,
      };

      trackEvent(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBED, subscribedEventData);

      // Get base key (e.g., "SUBSCRIBED_SUCCESSFULLY")
      const statusKey = getResponseMessageKey(response.code);

      // Use utility to get "NEWSLETTER.SUBSCRIBED_SUCCESSFULLY"
      const messageKey = getScopedMessageKey('NEWSLETTER', statusKey);

      overlay.showToast(tAppMessages(messageKey), {
        type: 'success',
        theme: 'colored',
        duration: 5000,
      });

      resetForm();
    } catch (error: unknown) {
      const errorCode = error instanceof HttpError ? error.code : undefined;

      const failedEventData = {
        page: window.location.pathname,
        location: 'footer',
        errorCode: errorCode ?? 'UNKNOWN_ERROR',
        errorType: error instanceof HttpError ? 'HttpError' : 'UnknownError',
      };

      trackEvent(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIPTION_FAILED, failedEventData);

      // Get base key (e.g., "SERVER_ERROR" or "TOO_MANY_REQUESTS")
      const statusKey = getResponseMessageKey(errorCode);

      const newsletterScopedKey = getScopedMessageKey('NEWSLETTER', statusKey);
      const commonScopedKey = `COMMON.${statusKey}`;

      // If NEWSLETTER section has the key, use it. Otherwise, fallback to COMMON.
      const messagePath = tAppMessages.has(newsletterScopedKey)
        ? newsletterScopedKey
        : commonScopedKey;

      overlay.showToast(tAppMessages(messagePath), {
        type: 'error',
        theme: 'colored',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigation = [
    {
      id: 'what-we-provide',
      label: t('navigation.provide'),
    },
    {
      id: 'why-it-works',
      label: t('navigation.works'),
    },
    {
      id: 'how-it-works',
      label: t('navigation.how'),
    },
    {
      id: 'who-its-for',
      label: t('navigation.who'),
    },
    {
      id: 'case-studies',
      label: t('navigation.caseStudies'),
    },
    {
      id: 'about',
      label: t('navigation.about'),
    },
    {
      id: 'contact',
      label: t('navigation.contact'),
    },
    {
      id: 'careers',
      label: t('navigation.careers'),
    },
  ];

  const products = t.raw('products') as string[];

  const contactItems = [
    {
      icon: FaIcons.location,
      content: (
        <span
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-top: -4px;
          `}
        >
          <span>{t('contact.addressLine1')}</span>
          <span>{t('contact.addressLine2')}</span>
        </span>
      ),
    },
    {
      icon: FaIcons.phone,
      content: t('contact.phone'),
    },
    {
      icon: FaIcons.email,
      content: t('contact.email'),
    },
    {
      icon: FaIcons.clock,
      content: t('contact.hours'),
    },
  ];

  const NAVBAR_SCROLL_OFFSET = 72;

  const handleNavigationClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(elementTop - NAVBAR_SCROLL_OFFSET, 0);

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });

    window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <footer id="site-footer" css={styles.footer}>
      <Container customStyles={styles.container}>
        <FlexLayout
          direction="row"
          wrap="wrap"
          columns={12}
          gap={{
            default: 24,
            md: 32,
            lg: 24,
          }}
          justifyContent="space-between"
          padding={{ default: 0 }}
          fullWidth
        >
          {/* Column 1: Branding */}
          <FlexItem
            columnSpan={{
              default: 12,
              md: 6,
              lg: 6,
              xl: 2.5,
            }}
            basis={{
              default: '100%',
              md: 'calc(50% - 16px)',
              xl: 'calc(20% - 20px)',
            }}
          >
            <FlexLayout
              direction="column"
              gap={{
                default: 20,
                md: 24,
                lg: 36,
              }}
              padding={{ default: 0 }}
            >
              <FlexItem>
                <Logo
                  src="/images/wciLogo.png"
                  decorative={false}
                  href="/"
                  alt={t('logoAlt')}
                  width={isTablet ? 160 : 224}
                  height={isTablet ? 42 : 60}
                />
              </FlexItem>

              <FlexItem>
                <Typography variant="p" customStyles={styles.footerText}>
                  {t('description')}
                </Typography>
              </FlexItem>

              <FlexItem>
                <SocialMediaGroup
                  platforms={[
                    {
                      frontIcon: {
                        name: FaIcons.LinkedIn,
                      },
                      url: 'https://www.linkedin.com/company/wealthcraft-innovations',
                      backIcon: {
                        name: FaIcons.LinkedIn,
                        color: '#0077B5',
                      },
                      onClick: handleLinkedInClick,
                    },
                    {
                      frontIcon: {
                        name: FaIcons.Instagram,
                      },
                      url: 'https://www.instagram.com/wealthcraftinnovations',
                      backIcon: {
                        name: FaIcons.Instagram,
                        color: '#E4405F',
                      },
                      onClick: handleInstagramClick,
                    },
                    {
                      frontIcon: {
                        name: FaIcons.Twitter,
                      },
                      url: 'https://x.com/wci_tech',
                      backIcon: {
                        name: FaIcons.Twitter,
                        color: '#1DA1F2',
                      },
                      onClick: handleTwitterClick,
                    },
                    {
                      frontIcon: {
                        name: FaIcons.youTube,
                      },
                      url: 'https://www.youtube.com/@wealthcraftinnovations2024',
                      backIcon: {
                        name: FaIcons.youTube,
                        color: '#FF0000',
                      },
                      onClick: handleYouTubeClick,
                    },
                  ]}
                  color={isDark ? '#182748' : '#ffffff'}
                  size={isDesktop ? 24 : isTablet ? 16 : 16}
                  layout="flip"
                  customStyles={{
                    wrapper: styles.socialMediaWrapper,
                    container: styles.socialMediaContainer,
                  }}
                />
              </FlexItem>
            </FlexLayout>
          </FlexItem>

          {/* Column 2: Navigation */}
          <FlexItem
            columnSpan={{
              default: 12,
              md: 3,
              lg: 3,
              xl: 1.5,
            }}
            basis={{
              default: '100%',
              md: '10%',
              lg: '10%',
            }}
          >
            <FlexLayout
              direction="column"
              gap={{
                default: 16,
                md: 14,
                lg: 16,
              }}
              padding={{ default: 0 }}
            >
              <FlexItem>
                <Typography variant="h3" customStyles={styles.footerHeading}>
                  {t('navHeading')}
                </Typography>
              </FlexItem>

              <FlexItem>
                <ul css={styles.servicesList} role="list">
                  {navigation.map((item, index) => {
                    const isComingSoon = item.id === 'case-studies' || item.id === 'careers';

                    return (
                      <li key={index}>
                        <Link
                          url={isComingSoon ? '/coming-soon' : `/#${item.id}`}
                          label={item.label}
                          variant={isDark ? 'secondary' : 'primary'}
                          effect="underline"
                          {...(!isComingSoon && {
                            onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
                              handleNavigationClick(event, item.id),
                          })}
                          customStyles={{
                            link: styles.footerText,
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </FlexItem>
            </FlexLayout>
          </FlexItem>

          {/* Column 3: Products */}
          <FlexItem
            columnSpan={{
              default: 12,
              md: 3,
              lg: 3,
              xl: 2,
            }}
            basis={{
              default: '100%',
              md: '12%',
              lg: '12%',
            }}
          >
            <FlexLayout
              direction="column"
              gap={{
                default: 16,
                md: 16,
                lg: 16,
              }}
              padding={{ default: 0 }}
            >
              <FlexItem>
                <Typography variant="h3" customStyles={styles.footerHeading}>
                  {t('productsHeading')}
                </Typography>
              </FlexItem>

              <FlexItem>
                <ul css={styles.servicesList} role="list">
                  {products.map((product, index) => {
                    const targetId = product.toLowerCase().replace(/\s+/g, '');

                    return (
                      <li key={index}>
                        <Link
                          url={`/platform#${targetId}`}
                          label={product}
                          variant={isDark ? 'secondary' : 'primary'}
                          effect="underline"
                          customStyles={{
                            link: styles.footerText,
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </FlexItem>
            </FlexLayout>
          </FlexItem>

          {/* Column 4: Contact */}
          <FlexItem
            columnSpan={{
              default: 12,
              md: 6,
              lg: 6,
              xl: 3,
            }}
            basis={{
              default: '100%',
              md: '26%',
              lg: '26%',
            }}
          >
            <FlexLayout
              direction="column"
              gap={{
                default: 20,
                md: 16,
                lg: 24,
              }}
              padding={{ default: 0 }}
            >
              <FlexItem>
                <Typography variant="h3" customStyles={styles.footerHeading}>
                  {t('contactHeading')}
                </Typography>
              </FlexItem>

              <FlexItem>
                <FlexLayout direction="column" gap={16} padding={{ default: 0 }}>
                  {contactItems.map((item, index) => (
                    <FlexItem key={index}>
                      <IconContent
                        withWrap
                        iconVerticalAlignment="start"
                        icon={<Icon name={item.icon} size={isDesktop ? 24 : isTablet ? 16 : 24} />}
                        customStyles={{
                          base: styles.contactIconBase,
                          left: styles.contactIconLeft,
                          content: styles.footerText,
                        }}
                      >
                        {item.content}
                      </IconContent>
                    </FlexItem>
                  ))}
                </FlexLayout>
              </FlexItem>
            </FlexLayout>
          </FlexItem>

          {/* Column 5: Newsletter */}
          <FlexItem
            columnSpan={{
              default: 12,
              md: 6,
              lg: 6,
              xl: 3,
            }}
            basis={{
              default: '100%',
              md: '32%',
              lg: '32%',
            }}
          >
            <FlexLayout
              direction="column"
              gap={{
                default: 24,
                md: 16,
                lg: 36,
              }}
              padding={{ default: 0 }}
            >
              <FlexItem>
                <Typography variant="h3" customStyles={styles.footerHeading}>
                  {t('newsletterHeading')}
                </Typography>
              </FlexItem>

              <FlexItem>
                <Typography variant="p" customStyles={styles.footerText}>
                  {t('newsletterText')}
                </Typography>
              </FlexItem>

              <FlexItem>
                <Form onSubmit={handleNewsletterSubmit}>
                  <div css={styles.textFieldContainer}>
                    <TextField
                      id="newsletterEmail"
                      name="newsletterEmail"
                      appearance="filled"
                      shape="pill"
                      alignMessageWithField
                      inputType="email"
                      required
                      label={t('emailLabel')}
                      customStyles={{
                        container: styles.newsLetterTextFieldContainer,
                      }}
                    />

                    <div css={styles.textFieldButtonWrapper}>
                      <Button
                        type="submit"
                        variant={isDark ? 'secondary' : 'primary'}
                        ariaLabel="subscribe"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        onClick={handleNewsletterSubscribeClick}
                        icon={
                          <Icon
                            name={IoIosSend}
                            iconPath={SvgIcons.textFieldIcon.path}
                            size={isTablet ? 20 : 24}
                            color={isDark ? 'black' : 'white'}
                          />
                        }
                        loadingConfig={{
                          loadingIndicatorProps: {
                            spinnerType: 'mesh-gradient',
                            size: 28,
                          },
                        }}
                        customStyles={{
                          base: styles.textFieldButton,
                        }}
                      />
                    </div>
                  </div>
                </Form>
              </FlexItem>
            </FlexLayout>
          </FlexItem>
        </FlexLayout>

        {/* Bottom */}
        <div css={styles.copyrightContainer}>
          <Typography variant="span" customStyles={styles.footerText}>
            {t('copyright')}
          </Typography>

          <Typography variant="span" customStyles={styles.copyrightCompanyName}>
            {t('companyName')}
          </Typography>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
